import { DOCUMENT } from '@angular/common';
import { Injectable, Renderer2, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ISEO } from '../http/http.interface';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  protected readonly _meta: Meta = inject(Meta);
  protected readonly _titleService: Title = inject(Title);
  protected readonly _document: Document = inject(DOCUMENT);

  public script?: HTMLScriptElement;

  public setSeoTags(seo?: ISEO, ogType: string = ''): void {
    if (!seo) {
      return;
    }

    const {
      metaTitle,
      metaRobots,
      metaDescription,
      keywords,
      metaImage,
    } = seo;

    this._titleService.setTitle(metaTitle || '');

    const seoList = [
      {
        name: 'robots',
        content: metaRobots || 'index, follow',
      }
    ];

    if (ogType) {
      seoList.push({
        name: 'og:type',
        content: ogType,
      });
    }

    if (metaTitle) {
      seoList.push({
        name: 'og:title',
        content: metaTitle,
      });
    }

    if (metaDescription) {
      seoList.push({
        name: 'description',
        content: metaDescription,
      });
    }

    if (metaDescription) {
      seoList.push({
        name: 'og:description',
        content: metaDescription,
      });
    }

    if (keywords) {
      seoList.push({
        name: 'keywords',
        content: keywords,
      });
    }

    if (metaImage?.data) {
      const {
        url,
        width,
        height,
        alternativeText,
        mime,
      } = metaImage.data.attributes;

      if (url) {
        seoList.push({
          name: 'og:image',
          content: url,
        });
      }

      if (width.toString()) {
        seoList.push({
          name: 'og:image:width',
          content: width.toString(),
        });
      }

      if (height.toString()) {
        seoList.push({
          name: 'og:image:height',
          content: height.toString(),
        });
      }

      if (alternativeText) {
        seoList.push({
          name: 'og:image:alt',
          content: alternativeText,
        });
      }

      if (mime) {
        seoList.push({
          name: 'og:image:type',
          content: mime,
        });
      }
    }

    this._meta.addTags(seoList);
  }

  public setStructuredData(renderer: Renderer2, jsonLD?: { [key: string]: any }): void {
    if (!jsonLD) {
      return;
    }

    this.script = renderer.createElement('script');
    this.script!.setAttribute('id', 'json-ld');
    this.script!.setAttribute('type', 'application/ld+json');
    this.script!.innerHTML = JSON.stringify(jsonLD, null, 2);

    renderer.appendChild(this._document.body, this.script);
  }

  public destroySeoTags(): void {
    this._meta.removeTag('name="og:title"');
    this._meta.removeTag('name="description"');
    this._meta.removeTag('name="og:description"');
    this._meta.removeTag('name="keywords"');
    this._meta.removeTag('name="robots"');
    this._meta.removeTag('name="og:image"');
    this._meta.removeTag('name="og:image:width"');
    this._meta.removeTag('name="og:image:height"');
    this._meta.removeTag('name="og:image:alt"');
    this._meta.removeTag('name="og:image:type"');
    this._meta.removeTag('name="og:type"');
  }

  public destroyStructuredData(): void {
    this._document.getElementById('json-ld')?.remove();
  }
}
